import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { parseFinanceDeepLink } from '@/pages/modules/finance/shared/deepLink';

export interface LegacyBillClientRoute {
  path?: string;
  redirect?: string;
  element?: React.ReactNode;
  children?: LegacyBillClientRoute[];
}

const editableDocumentPaths = new Set([
  '/document/invoice-reminder',
  '/document/customs-declaration-self-made',
]);

function LegacyBillRedirect({ basePath }: { basePath: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const finance = basePath.startsWith('/finance/');
  const hasIdentity = finance
    ? parseFinanceDeepLink(params) !== null
    : Boolean(params.get('id'));
  const acceptsNewEdit = finance || editableDocumentPaths.has(basePath)
    || basePath === '/cost/expense-registration-ledger';
  const detail = hasIdentity || (acceptsNewEdit && params.get('mode') === 'edit');

  return <Navigate replace to={{
    pathname: `${basePath}/${detail ? 'detail' : 'list'}`,
    search: location.search,
    hash: location.hash,
  }} />;
}

/** Keep legacy bill URLs compatible while retaining the split list/detail routes. */
export function patchLegacyBillClientRoutes({ routes }: { routes: LegacyBillClientRoute[] }): void {
  const allRoutes: LegacyBillClientRoute[] = [];
  const visit = (entries: LegacyBillClientRoute[]) => {
    for (const route of entries) {
      allRoutes.push(route);
      if (route.children) visit(route.children);
    }
  };
  visit(routes);
  const paths = new Set(allRoutes.map(route => route.path));
  for (const route of allRoutes) {
    const basePath = route.path;
    if (!basePath || !/^\/(finance|document|cash|cost)\/[^/]+$/.test(basePath)) continue;
    const target = route.redirect ?? (React.isValidElement<{ to?: unknown }>(route.element)
      ? route.element.props.to : undefined);
    if (target !== `${basePath}/list` || !paths.has(`${basePath}/detail`)) continue;
    // Umi has already created the redirect element; preserve every route option.
    route.element = <LegacyBillRedirect basePath={basePath} />;
  }
}
